/**
 * src/modules/legacy-modals.ts — Sprint 20.2 Etap 3 ekstrakcji app.js (2026-05-02 SESSION 14).
 *
 * Modal management — wyciągnięte z app.js IIFE.
 *   - openModal(title, bodyHtml, footerHtml, wide)
 *   - closeModal()
 *   - showConfirm(title, message, opts) → Promise<boolean>  (delegat do sb-dialogs.ts)
 *   - openDrawerEditPanel(title, bodyHtml, footerHtml)
 *   - closeDrawerEditPanel()
 *
 * Strangler pattern: TS exposes window globals only if app.js IIFE jeszcze ich nie zdefiniował.
 * Po pełnej migracji można usunąć z app.js.
 */

import { showConfirm as sbShowConfirm } from './sb-dialogs.ts';
import type { SbConfirmOptions } from './sb-dialogs.ts';

declare const lucide: any;
declare const escapeHtml: (s: any) => string;

let _modalUrlRestore: string | null = null;
let _modalFocusRestore: HTMLElement | null = null;

const _MODAL_LAYER_FLOOR = 10050;

function _visibleDrawerLayer(): { open: boolean; maxZ: number } {
  const selectors = [
    '#order-drawer.open',
    '#return-drawer.open',
    '#issue-drawer',
    '#nested-order-drawer.open',
    '.product-drawer.open',
    '.invoice-drawer.open',
    '.return-drawer.open',
    '.issue-drawer.open',
  ];
  let maxZ = 0;
  let open = false;
  document.querySelectorAll<HTMLElement>(selectors.join(',')).forEach((el) => {
    if (getComputedStyle(el).display === 'none') return;
    open = true;
    const z = Number.parseInt(getComputedStyle(el).zIndex || '0', 10);
    if (Number.isFinite(z)) maxZ = Math.max(maxZ, z);
  });
  return { open, maxZ };
}

function _getState(): any {
  const w = window as any;
  return (w.SellBaseApp && w.SellBaseApp.state) || {};
}

export function openDrawerEditPanel(title?: string, bodyHtml?: string, footerHtml?: string): boolean {
  const panel = document.getElementById('drawer-edit-panel');
  const titleEl = document.getElementById('drawer-edit-panel-title');
  const bodyEl = document.getElementById('drawer-edit-panel-body');
  const footerEl = document.getElementById('drawer-edit-panel-footer');
  if (!panel) return false;
  if (titleEl) titleEl.textContent = title || 'Edytuj';
  if (bodyEl) bodyEl.innerHTML = bodyHtml || '';
  if (footerEl) footerEl.innerHTML = footerHtml || '';
  panel.classList.add('open');
  if (typeof lucide !== 'undefined') lucide.createIcons();
  const closeBtn = document.getElementById('drawer-edit-panel-close');
  if (closeBtn) {
    (closeBtn as HTMLElement).onclick = () => closeDrawerEditPanel();
  }
  return true;
}

export function closeDrawerEditPanel(): void {
  const panel = document.getElementById('drawer-edit-panel');
  if (panel) panel.classList.remove('open');
}

export function openModal(title: string, bodyHtml: string, footerHtml?: string, wide?: boolean): void {
  // V1 only: jeśli drawer otwarty i ekran szeroki → użyj lewego panelu
  const isV2 = document.documentElement.hasAttribute('data-v2');
  const state = _getState();
  if (!isV2 && state.ordersDrawerOpen && window.innerWidth > 1100) {
    const opened = openDrawerEditPanel(title, bodyHtml, footerHtml);
    if (opened) return;
  }
  const overlay = document.getElementById('modal-overlay');
  const titleEl = document.getElementById('modal-title');
  const bodyEl = document.getElementById('modal-body');
  const footerEl = document.getElementById('modal-footer');
  const modalBox = document.getElementById('modal-card');
  if (titleEl) titleEl.textContent = title;
  if (bodyEl) bodyEl.innerHTML = bodyHtml;
  if (footerEl) footerEl.innerHTML = footerHtml || '';
  if (wide && modalBox) {
    modalBox.classList.add('modal-wide');
  } else if (modalBox) {
    modalBox.classList.remove('modal-wide');
  }
  if (overlay) {
    const drawerLayer = _visibleDrawerLayer();
    _modalFocusRestore = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    // Modal wywołany z dowolnego drawera zawsze jest najwyższą warstwą.
    // Dynamiczny z-index zachowuje LIFO także dla zagnieżdżonych drawerów.
    overlay.style.setProperty(
      'z-index',
      String(Math.max(_MODAL_LAYER_FLOOR, drawerLayer.maxZ + 10)),
      'important',
    );
    overlay.dataset.closeOnBackdrop = drawerLayer.open ? '1' : '0';
    overlay.classList.add('active');
    requestAnimationFrame(() => {
      const focusTarget = overlay.querySelector<HTMLElement>(
        '[autofocus], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])',
      );
      focusTarget?.focus();
    });
  }
  if (typeof lucide !== 'undefined') lucide.createIcons();
}

/**
 * Potwierdzenie SellBase — od 15.09.2026 (pkt 16) cienki delegat do `sb-dialogs.ts`.
 *
 * Renderowanie do `#modal-overlay` odpadło: nie umiało pokazać się nad oknem otwartym przez
 * `showModal()` (top-layer), gubiło obietnicę przy Escape/kliku w tło i wpinało przyciski przez
 * `setTimeout(…, 50)`. Sygnatura bez zmian, więc ~30 istniejących wywołań dostaje nowe okienko
 * za darmo.
 */
export function showConfirm(title: string, message: string, opts?: SbConfirmOptions): Promise<boolean> {
  return sbShowConfirm(title, message, opts);
}

export { showAlert, showPrompt } from './sb-dialogs.ts';

export function closeModal(): void {
  closeDrawerEditPanel();
  const overlay = document.getElementById('modal-overlay');
  const modalBox = document.getElementById('modal-card');
  if (modalBox) {
    modalBox.classList.remove('modal-wide');
    modalBox.classList.remove('product-editor-modal');
    modalBox.style.maxWidth = '';
  }
  if (overlay) {
    overlay.classList.remove('active');
    overlay.style.removeProperty('z-index');
    delete overlay.dataset.closeOnBackdrop;
  }
  if (_modalUrlRestore) {
    history.replaceState(null, '', _modalUrlRestore);
    _modalUrlRestore = null;
  }
  const focusRestore = _modalFocusRestore;
  _modalFocusRestore = null;
  if (focusRestore && document.contains(focusRestore)) {
    // 19.10.2026 (właściciel: po zgłoszeniu braku strona „scrolluje na sam dół”): fokus wraca do przycisku, który
    // otworzył okno, ale BEZ przewijania — lista pod oknem bywa w tym czasie przeładowywana i przeglądarka
    // przewijała stronę do elementu, który właśnie zmienia miejsce.
    requestAnimationFrame(() => {
      if (!focusRestore.isConnected) return;
      try { focusRestore.focus({ preventScroll: true }); } catch (_e) { /* element nieaktywny */ }
    });
  }
}

export function setModalUrlRestore(url: string | null): void {
  _modalUrlRestore = url;
}

// ── Inline invoice_name editor (Sesja 4 Krok 1 — migrated from app.js) ──
// @ts-ignore
export function _openInvoiceNameEditor(badge: any): void {
  const w = window as any;
  const SellBaseAPI = w.SellBaseAPI;
  const showToast = w.showToast;
  // Close any existing editor
  const existing = document.querySelector('.invoice-name-editor-popup');
  if (existing) existing.remove();

  const productId = badge.dataset.productId;
  const currentName = badge.dataset.invoiceName || '';
  const rect = badge.getBoundingClientRect();

  const popup = document.createElement('div');
  popup.className = 'invoice-name-editor-popup';
  popup.style.cssText = 'position:fixed;z-index:9999;background:var(--color-surface);border:1px solid var(--color-border);border-radius:8px;box-shadow:var(--shadow-lg);padding:10px;display:flex;gap:6px;align-items:center;top:' + (rect.bottom + 4) + 'px;left:' + rect.left + 'px;min-width:320px';

  const input = document.createElement('input');
  input.type = 'text';
  input.value = currentName;
  input.placeholder = 'Nazwa na fakturze...';
  input.style.cssText = 'flex:1;padding:6px 8px;border:1px solid var(--color-border);border-radius:6px;font-size:12px;background:var(--color-bg);color:var(--color-text);outline:none;min-width:200px';

  const saveBtn = document.createElement('button');
  saveBtn.textContent = 'Zapisz';
  saveBtn.style.cssText = 'padding:6px 12px;border:none;border-radius:6px;background:var(--color-primary);color:#fff;font-size:12px;cursor:pointer;white-space:nowrap';

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = '✕';
  cancelBtn.style.cssText = 'padding:6px 8px;border:none;border-radius:6px;background:var(--color-surface-hover,rgba(255,255,255,.1));color:var(--color-text-muted);font-size:12px;cursor:pointer';

  popup.appendChild(input);
  popup.appendChild(saveBtn);
  popup.appendChild(cancelBtn);
  document.body.appendChild(popup);

  input.focus();
  input.select();

  async function save() {
    const newName = input.value.trim();
    if (newName === currentName) { popup.remove(); return; }
    saveBtn.disabled = true;
    saveBtn.textContent = '...';
    try {
      await SellBaseAPI.updateProduct(productId, { invoice_name: newName });
      badge.dataset.invoiceName = newName;
      badge.title = 'Nazwa na fakturze: ' + newName + ' — kliknij aby edytować';
      // Update badge text (keep icon)
      const icon = badge.querySelector('i');
      badge.textContent = '';
      if (icon) { badge.appendChild(icon); badge.appendChild(document.createTextNode(' ')); }
      badge.appendChild(document.createTextNode(newName || '(brak)'));
      if (showToast) showToast('Zapisano nazwę na fakturze', 'success');
      popup.remove();
    } catch (e: any) {
      if (showToast) showToast('Błąd zapisu: ' + (e.message || e), 'error');
      saveBtn.disabled = false;
      saveBtn.textContent = 'Zapisz';
    }
  }

  saveBtn.addEventListener('click', save);
  input.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') popup.remove();
  });
  cancelBtn.addEventListener('click', function() { popup.remove(); });

  // Close on outside click (delay to avoid catching the opening click)
  let _editorReady = false;
  setTimeout(function() { _editorReady = true; }, 200);
  document.addEventListener('click', function _closeEditor(e) {
    if (!_editorReady) return;
    if (!popup.contains(e.target as any) && !badge.contains(e.target as any)) {
      popup.remove();
      document.removeEventListener('click', _closeEditor);
    }
  });
}

// ── Generic inline field editor (Sesja 4 Krok 1 — migrated from app.js) ──
export function _openFieldEditor(el: any): void {
  const w = window as any;
  const SellBaseAPI = w.SellBaseAPI;
  const SellBaseData = w.SellBaseData;
  const showToast = w.showToast;
  const existing = document.querySelector('.inline-field-editor-popup');
  if (existing) existing.remove();

  const productId = el.dataset.productId;
  const field = el.dataset.field;
  const currentVal = el.dataset.value || '';
  const label = el.dataset.label || field;
  const inputType = el.dataset.inputType || 'text';
  const rect = el.getBoundingClientRect();

  const popup = document.createElement('div');
  popup.className = 'inline-field-editor-popup';
  popup.style.cssText = 'position:fixed;z-index:9999;background:var(--color-surface);border:1px solid var(--color-border);border-radius:8px;box-shadow:var(--shadow-lg);padding:10px;display:flex;gap:6px;align-items:center;top:' + (rect.bottom + 4) + 'px;left:' + Math.max(rect.left - 50, 10) + 'px;min-width:220px';

  const input = document.createElement('input');
  input.type = inputType;
  input.value = currentVal;
  input.placeholder = label + '...';
  if (inputType === 'number') { (input as any).step = 'any'; (input as any).min = '0'; }
  input.style.cssText = 'flex:1;padding:6px 8px;border:1px solid var(--color-border);border-radius:6px;font-size:12px;background:var(--color-bg);color:var(--color-text);outline:none;min-width:100px';

  const saveBtn = document.createElement('button');
  saveBtn.textContent = 'Zapisz';
  saveBtn.style.cssText = 'padding:6px 12px;border:none;border-radius:6px;background:var(--color-primary);color:#fff;font-size:12px;cursor:pointer;white-space:nowrap';

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = '✕';
  cancelBtn.style.cssText = 'padding:6px 8px;border:none;border-radius:6px;background:var(--color-surface-hover,rgba(255,255,255,.1));color:var(--color-text-muted);font-size:12px;cursor:pointer';

  popup.appendChild(input);
  popup.appendChild(saveBtn);
  popup.appendChild(cancelBtn);
  document.body.appendChild(popup);
  input.focus();
  input.select();

  async function save() {
    const newVal = input.value.trim();
    if (newVal === currentVal) { popup.remove(); return; }
    saveBtn.disabled = true;
    saveBtn.textContent = '...';
    try {
      const payload: any = {};
      if (field === 'quantity') {
        payload.quantity = parseFloat(newVal) || 0;
        // 2026-07-20 (audyt magazynu #8): optymistyczna blokada — wysyłamy stan, który
        // user WIDZIAŁ przy otwarciu edytora; sync w tle zdjął sztuki → backend 409
        // zamiast cichego cofnięcia sprzedaży.
        payload.expected_quantity = parseFloat(currentVal) || 0;
      }
      else if (field === 'price_net') payload.price_net = parseFloat(newVal) || 0;
      else if (field === 'location') payload.location = newVal;
      await SellBaseAPI.updateProduct(productId, payload);
      el.dataset.value = newVal;
      // Update displayed text
      if (field === 'quantity') {
        const q = parseInt(newVal, 10) || 0;
        const cls = q > 10 ? 'high' : q > 0 ? 'medium' : 'low';
        el.className = 'stock-indicator ' + cls + ' editable-field';
        el.textContent = q;
      } else if (field === 'price_net') {
        el.textContent = SellBaseData.formatCurrency(parseFloat(newVal) || 0);
      } else {
        el.textContent = newVal || '—';
      }
      if (showToast) showToast('Zapisano ' + label, 'success');
      popup.remove();
    } catch (e: any) {
      // 2026-07-20 (audyt magazynu #8): 409 = stan zmienił się w tle (sprzedaż zdjęła
      // sztuki między renderem a zapisem) — odśwież listę zamiast nadpisywać na ślepo.
      if (e && (e.status === 409 || String(e.message || '').indexOf('Stan zmienił się') === 0)) {
        if (showToast) showToast((e.message || 'Stan zmienił się w tle') + ' — odświeżam listę', 'warning');
        popup.remove();
        try { (window as any).loadProductsPage && (window as any).loadProductsPage(); } catch (_) { /* ignore */ }
        return;
      }
      if (showToast) showToast('Błąd zapisu: ' + (e.message || e), 'error');
      saveBtn.disabled = false;
      saveBtn.textContent = 'Zapisz';
    }
  }

  saveBtn.addEventListener('click', save);
  input.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') popup.remove();
  });
  cancelBtn.addEventListener('click', function() { popup.remove(); });

  let _editorReady = false;
  setTimeout(function() { _editorReady = true; }, 200);
  document.addEventListener('click', function _closeEditor(e) {
    if (!_editorReady) return;
    if (!popup.contains(e.target as any) && !el.contains(e.target as any)) {
      popup.remove();
      document.removeEventListener('click', _closeEditor);
    }
  });
}

// Strangler: window expose tylko gdy app.js IIFE jeszcze ich nie zdefiniowało.
if (typeof window !== 'undefined') {
  const w = window as any;
  if (typeof w.openModal !== 'function') w.openModal = openModal;
  if (typeof w.closeModal !== 'function') w.closeModal = closeModal;
  if (typeof w.showConfirm !== 'function') w.showConfirm = showConfirm;
  if (typeof w.openDrawerEditPanel !== 'function') w.openDrawerEditPanel = openDrawerEditPanel;
  if (typeof w.closeDrawerEditPanel !== 'function') w.closeDrawerEditPanel = closeDrawerEditPanel;
  // Sprint 20.2 HARD cleanup — extended strangler: SellBaseApp + __sbExports
  if (!w.SellBaseApp) w.SellBaseApp = {};
  if (typeof w.SellBaseApp.openModal !== 'function') w.SellBaseApp.openModal = openModal;
  if (typeof w.SellBaseApp.closeModal !== 'function') w.SellBaseApp.closeModal = closeModal;
  if (!w.__sbExports) w.__sbExports = {};
  if (typeof w.__sbExports.openModal !== 'function') w.__sbExports.openModal = openModal;
  if (typeof w.__sbExports.closeModal !== 'function') w.__sbExports.closeModal = closeModal;
  if (typeof w.__sbExports.showConfirm !== 'function') w.__sbExports.showConfirm = showConfirm;
  // Sesja 4 Krok 1 — migrated z app.js: inline editors
  w._openInvoiceNameEditor = _openInvoiceNameEditor;
  w._openFieldEditor = _openFieldEditor;
  w.SellBaseApp._openInvoiceNameEditor = _openInvoiceNameEditor;
  w.SellBaseApp._openFieldEditor = _openFieldEditor;
  w.__sbExports._openInvoiceNameEditor = _openInvoiceNameEditor;
  w.__sbExports._openFieldEditor = _openFieldEditor;
}
